import { useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import React, { useMemo, useCallback } from 'react';

import AdvancedHotkey from './component';
import Global from '../../../store/global-selectors';
import { useScales } from '../../../hooks/scales/use-scales';
import { useIsReadWeightFromScaleDisabled } from '../../../hooks/scales/use-is-read-weight-from-scale-disabled';
import { readWeightFromScaleHotkeySubject } from '../../../fulfillment-planner/hooks/use-packages-state';
import { useReadFromScale } from '../../../hooks/use-read-from-scale';

export const messages = defineMessages({
  name: {
    description: 'read weight from scale hotkey',
    defaultMessage: 'Read weight from scale',
    id: 'cEfXle',
  },
});

export const hotkey = ['w'];
export const barcodeContent = '+2D0CD19741AC=';

const ReadWeightFromScaleHotkey = ({ children, selectedFulfillmentPlanIds }) => {
  const intl = useIntl();
  const fulfillmentPlanId = selectedFulfillmentPlanIds[0];
  const packageCount = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'packages.length',
    }),
  );

  const { enabledScalesGroupedByWorkstation, scale } = useScales();
  const { isDisabled } = useIsReadWeightFromScaleDisabled(enabledScalesGroupedByWorkstation.length, packageCount);

  const onScaleRead = (newValues) => {
    const { weight, dims } = newValues;
    readWeightFromScaleHotkeySubject.next({
      unit: weight.unit,
      value: weight.value,
      dims,
      fulfillmentPlanId,
    });
  };

  const onReadFromScale = useReadFromScale(onScaleRead);
  const onHandle = useCallback(() => onReadFromScale(scale), [onReadFromScale, scale]);

  const shouldReadScaleWeight = scale && selectedFulfillmentPlanIds.length === 1 && !isDisabled;

  const hotkeyMetadata = useMemo(
    () => ({
      barcode: barcodeContent,
      displayHotkeyStrings: hotkey,
      hotkey,
      handler: onHandle,
      name: intl.formatMessage(messages.name),
      legacyCategory: 'general',
      doNotTrack: true,
    }),
    [intl, onHandle],
  );

  return (
    <>
      {shouldReadScaleWeight && <AdvancedHotkey hotkey={hotkeyMetadata} />}
      {children}
    </>
  );
};

ReadWeightFromScaleHotkey.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  selectedFulfillmentPlanIds: PropTypes.array.isRequired,
};

export default ReadWeightFromScaleHotkey;
